import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Loader2, MapPinOff, Move } from "lucide-react";
import { getCondominiosMapa, setCondominioLocalizacao, resetCondominioLocalizacao } from "@/lib/api";
import { useAuth, hasPermission } from "@/lib/auth";
import MapaLegenda from "./MapaLegenda";
import MapaPopup from "./MapaPopup";
import MapaToolbar from "./MapaToolbar";
import HeatLayer from "./HeatLayer";
import { STATUS_CATS, NO_CONSULTOR_COLOR, statusOf, consultorColors, pinIcon, isApprox } from "./mapaUtils";

const SEM = "__sem__";
const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.id).join(",");
  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [50, 50], maxZoom: 15 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function useMapaData() {
  const [data, setData] = useState(null);
  const load = useCallback(() => getCondominiosMapa().then(setData).catch(() => setData((d) => d || { items: [] })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.geocodificando) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);
  return { data, load };
}

function usePinAdjust(load) {
  const save = useCallback(async (item, lat, lng) => {
    const prev = { lat: item.lat, lng: item.lng, precisao: item.precisao };
    try {
      await setCondominioLocalizacao(item.id, lat, lng);
      await load();
      toast.success(`Posição de "${item.condominio}" atualizada`, {
        action: {
          label: "Desfazer",
          onClick: async () => {
            await setCondominioLocalizacao(item.id, prev.lat, prev.lng, prev.precisao || "manual");
            load();
          },
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Não foi possível salvar a posição");
      load();
    }
  }, [load]);
  const reset = useCallback(async (item) => {
    await resetCondominioLocalizacao(item.id);
    toast.success("Voltando à localização automática…");
    load();
  }, [load]);
  return { save, reset };
}

export default function CondominiosMapa({ condominios, onOpenPanel }) {
  const { user } = useAuth();
  const canEdit = hasPermission(user, "condominios.edit");
  const { data, load } = useMapaData();
  const { save, reset } = usePinAdjust(load);
  const [mode, setMode] = useState("situacao");
  const [hidden, setHidden] = useState(new Set());
  const [heatOn, setHeatOn] = useState(false);
  const [heatMetric, setHeatMetric] = useState("clientes");
  const [editMode, setEditMode] = useState(false);

  const byId = useMemo(() => Object.fromEntries(condominios.map((c) => [c.id, c])), [condominios]);
  const visiveis = useMemo(() => (data?.items || []).filter((i) => byId[i.id]), [data, byId]);
  const colors = useMemo(() => consultorColors(new Set(visiveis.map((i) => i.consultor).filter(Boolean))), [visiveis]);
  const groupOf = useCallback((i) => (mode === "situacao" ? statusOf(i) : i.consultor || SEM), [mode]);
  const colorOf = (i) => (mode === "situacao" ? STATUS_CATS.find((s) => s.key === statusOf(i)).color : colors[i.consultor] || NO_CONSULTOR_COLOR);

  const entries = useMemo(() => {
    const count = (k) => visiveis.filter((i) => i.lat != null && groupOf(i) === k).length;
    if (mode === "situacao") return STATUS_CATS.map((s) => ({ ...s, count: count(s.key) }));
    return [...Object.entries(colors).map(([n, color]) => ({ key: n, label: n, color, count: count(n) })),
      { key: SEM, label: "Sem consultor", color: NO_CONSULTOR_COLOR, count: count(SEM) }];
  }, [visiveis, colors, mode, groupOf]);

  const points = visiveis.filter((i) => i.lat != null && !hidden.has(groupOf(i)));
  const heatPoints = useMemo(() => visiveis
    .filter((i) => i.lat != null)
    .map((i) => [i.lat, i.lng, Number(heatMetric === "clientes" ? i.clientes_fechados : i.vendas_realizadas) || 0])
    .filter((p) => p[2] > 0), [visiveis, heatMetric]);
  const semLocal = visiveis.filter((i) => i.lat == null);
  const switchMode = (m) => { setMode(m); setHidden(new Set()); };
  const toggle = (k) => setHidden((h) => { const n = new Set(h); n.has(k) ? n.delete(k) : n.add(k); return n; });

  return (
    <div className="space-y-3" data-testid="condominios-mapa">
      <MapaToolbar mode={mode} setMode={switchMode} heatOn={heatOn} setHeatOn={setHeatOn} heatMetric={heatMetric}
        setHeatMetric={setHeatMetric} canEdit={canEdit} editMode={editMode} setEditMode={setEditMode}
        geocodificando={data?.geocodificando} />

      {editMode && (
        <div className="rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-primary flex items-center gap-2" data-testid="mapa-ajuste-aviso">
          <Move className="w-4 h-4 shrink-0" /> Arraste qualquer pino até a posição exata do prédio. A mudança é salva ao soltar e pode ser desfeita.
        </div>
      )}

      <div className="relative isolate z-0 space-y-3 md:space-y-0">
        <div className={`flua-map relative h-[460px] md:h-[580px] rounded-2xl overflow-hidden border ${editMode ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
          {!data && <div className="absolute inset-0 z-[600] grid place-items-center bg-card"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
          <div className="h-full w-full" data-testid="mapa-leaflet">
          <MapContainer center={[-23.55, -46.63]} zoom={11} scrollWheelZoom className="h-full w-full">
            <TileLayer url={TILES} maxZoom={19}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            <FitBounds points={points} />
            {heatOn && <HeatLayer points={heatPoints} />}
            {points.map((i) => (
              <Marker key={`${i.id}-${i.lat}-${i.lng}-${editMode}`} position={[i.lat, i.lng]} draggable={editMode}
                icon={pinIcon(colorOf(i), isApprox(i.precisao))}
                eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); save(i, p.lat, p.lng); } }}>
                <Popup>
                  <MapaPopup item={i} status={statusOf(i)} canEdit={canEdit} onReset={() => reset(i)} onOpen={() => onOpenPanel(byId[i.id])} />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          </div>
          {heatOn && heatPoints.length === 0 && (
            <div className="absolute bottom-3 left-3 z-[500] rounded-lg bg-background/85 backdrop-blur px-3 py-2 text-xs" data-testid="mapa-calor-vazio">
              Nenhum condomínio com {heatMetric === "clientes" ? "clientes fechados" : "vendas realizadas"} nesta visão.
            </div>
          )}
        </div>
        <MapaLegenda title={mode === "situacao" ? "Situação" : "Consultores"} entries={entries} hidden={hidden} onToggle={toggle}
          heat={heatOn ? { metric: heatMetric, total: heatPoints.reduce((s, p) => s + p[2], 0) } : null} />
      </div>

      {semLocal.length > 0 && (
        <div className="rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="mapa-sem-localizacao">
          <MapPinOff className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-foreground">{semLocal.length} fora do mapa</span> (sem endereço ou endereço não encontrado):{" "}
            {semLocal.map((i) => (
              <button key={i.id} type="button" className="underline-offset-2 hover:underline mr-2" onClick={() => onOpenPanel(byId[i.id])} data-testid={`mapa-sem-local-${i.id}`}>
                {i.condominio}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
